import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useLazyGetPaymentResultQuery } from "../../../redux/actions/Customer/cartCustomer";

import { PaymentResult } from "../../../types/Request/CustomerRequest";

import { PaymentStatus } from "../../../utils/Customer/paymentConstants";

import PaymentLoadingCard from "../../../components/customer/PaymentLoadingCard";
import PaymentFailedCard from "../../../components/customer/PaymentFailedCard";
import PaymentSuccessCard from "../../../components/customer/PaymentSuccessCard";

const CustomerPaymentSuccessScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ----------------------------------------------------------
  // Read Transaction Reference
  //
  // The payment gateway can return the reference through:
  //
  // 1. URL query string
  // 2. React Router location state
  // ----------------------------------------------------------

  const queryRef = new URLSearchParams(location.search).get("ref");

  const stateRef =
    (location.state as any)?.transactionReference ||
    (location.state as any)?.TransactionReference;

  const transactionReference = queryRef || stateRef;

  // ----------------------------------------------------------
  // RTK Query
  // ----------------------------------------------------------

  const [getPaymentResult] = useLazyGetPaymentResultQuery();
const [failureReason, setFailureReason] = useState<string | null>(null);
  // ----------------------------------------------------------
  // Component State
  // ----------------------------------------------------------

  const [paymentResult, setPaymentResult] = useState<PaymentResult>();

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  // ----------------------------------------------------------
  // Polling Configuration
  // ----------------------------------------------------------

  const retryCountRef = useRef(0);

  const maxRetries = 15;

  const retryDelay = 2000;

  // ----------------------------------------------------------
  // Poll Payment Result
  //
  // Payment can be marked as Paid before the background
  // post-payment processing has completed.
  //
  // Therefore we continue polling until:
  //
  // 1. Payment failed
  // 2. Payment is Paid AND post-processing completed
  // 3. Maximum polling attempts are reached
  // ----------------------------------------------------------

  useEffect(() => {
    // --------------------------------------------------------
    // Reset polling state when transaction changes
    // --------------------------------------------------------

    retryCountRef.current = 0;

    // --------------------------------------------------------
    // Transaction reference is required
    // --------------------------------------------------------

    if (!transactionReference) {
      setLoading(false);

      setError("Payment reference was not found.");

      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;

    let isMounted = true;

    // --------------------------------------------------------
    // Poll Function
    // --------------------------------------------------------

    const poll = async () => {
      if (!isMounted) {
        return;
      }

      // ------------------------------------------------------
      // Count every polling attempt.
      //
      // This is important because a successful response such
      // as Paid + IsPostProcessed=false must also count toward
      // the maximum polling limit.
      // ------------------------------------------------------

      retryCountRef.current += 1;

      try {
        // ----------------------------------------------------
        // IMPORTANT:
        //
        // false = do not prefer cached value.
        //
        // Payment post-processing happens asynchronously, so
        // every polling attempt should be allowed to fetch
        // the latest payment result from the backend.
        // ----------------------------------------------------

        const result = await getPaymentResult(
          transactionReference,
          false,
        ).unwrap();

        if (!isMounted) {
          return;
        }

        // ----------------------------------------------------
        // Payment Failed
        // ----------------------------------------------------

        if (result.paymentStatus === PaymentStatus.Failed) {
          setLoading(false);
  setFailureReason(result.failureReason || 'Payment Failed');
          setError("Payment failed.");

          return;
        }

        // ----------------------------------------------------
        // Payment Successful
        // AND
        // Post-payment processing completed
        // ----------------------------------------------------

        if (
          result.paymentStatus === PaymentStatus.Paid &&
          result.isPostProcessed
        ) {
          setPaymentResult(result);

          setLoading(false);

          return;
        }

        // ----------------------------------------------------
        // Maximum polling attempts reached
        // ----------------------------------------------------

        if (retryCountRef.current >= maxRetries) {
          setLoading(false);

          setError(
            "Your payment was successful, but order processing is still in progress. Please check your orders shortly.",
          );

          return;
        }

        // ----------------------------------------------------
        // Payment is still processing
        //
        // Wait before checking again.
        // ----------------------------------------------------

        timer = setTimeout(poll, retryDelay);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        console.error("Payment result polling failed:", err);

        // ----------------------------------------------------
        // Maximum attempts reached after API errors
        // ----------------------------------------------------

        if (retryCountRef.current >= maxRetries) {
          setLoading(false);

          setError(
            "Unable to verify payment status. Please check your orders.",
          );

          return;
        }

        // ----------------------------------------------------
        // Temporary API/network error
        //
        // Retry after the configured delay.
        // ----------------------------------------------------

        timer = setTimeout(poll, retryDelay);
      }
    };

    // --------------------------------------------------------
    // Start polling immediately
    // --------------------------------------------------------

    poll();

    // --------------------------------------------------------
    // Cleanup
    //
    // Prevent:
    // - state updates after unmount
    // - pending polling timers
    // --------------------------------------------------------

    return () => {
      isMounted = false;

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [transactionReference]);

  // ----------------------------------------------------------
  // Continue Action
  // ----------------------------------------------------------

  const handleContinue = () => {
    if (!paymentResult) {
      return;
    }

    navigate(paymentResult.redirectRoute, {
      state: {
        activeTab: paymentResult.activeTab,

        orderId: paymentResult.orderId,
      },
    });
  };

  // ----------------------------------------------------------
  // UI
  // ----------------------------------------------------------

  return (
    <div className="px-4 lg:px-20 py-6 max-w-4xl mx-auto">
      {/* ----------------------------------------------------
          Payment still being processed
          ---------------------------------------------------- */}

      {loading && <PaymentLoadingCard />}

      {/* ----------------------------------------------------
          Payment failed / verification failed
          ---------------------------------------------------- */}

      {!loading && error && <PaymentFailedCard message={error}    failureReason={failureReason} />}

      {/* ----------------------------------------------------
          Payment successful
          ---------------------------------------------------- */}

      {!loading && paymentResult && !error && (
        <PaymentSuccessCard
          paymentResult={paymentResult}
          onContinue={handleContinue}
        />
      )}
    </div>
  );
};

export default CustomerPaymentSuccessScreen;
